import { useContext } from 'react';
import PropTypes from 'prop-types';
import Table from 'react-bootstrap/Table';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TRANSACTION_TYPES, WEBHOOK_DIRECTION, WEBHOOK_DIRECTION_OPTIONS } from 'components/constants';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';

// Create/edit form for one webhook: URL, secret and direction.
// In edit mode (webhook passed) it also renders Delete and Test buttons.
export const WebhookForm = ({ webhook, isLoading, onSubmit, onCancel, onDelete, onTest }) => {
  const i18n = useContext(i18nContext);
  const isEditMode = !!webhook;

  const directionOptions = WEBHOOK_DIRECTION_OPTIONS.map((direction) => ({
    key: direction,
    value: i18n.getMessage(`webhookTab.direction.${direction}`)
  }));

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      webhookUrl: webhook?.webhookUrl || '',
      webhookSecret: '',
      direction: webhook?.direction || WEBHOOK_DIRECTION.BOTH
    },
    validationSchema: Yup.object().shape({
      webhookUrl: Yup.string()
        .required(i18n.getMessage('customer.validation.isRequired'))
        .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
        .url(i18n.getMessage('customer.validation.isUrl')),
      webhookSecret: Yup.string().max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
      direction: Yup.string()
        .oneOf(WEBHOOK_DIRECTION_OPTIONS)
        .required(i18n.getMessage('customer.validation.isRequired'))
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        webhookUrl: values.webhookUrl,
        direction: values.direction
      };

      // Only include webhookSecret if it's not empty, backend keeps the existing one otherwise
      if (values.webhookSecret && values.webhookSecret.trim() !== '') {
        payload.webhookSecret = values.webhookSecret;
      }

      const isSuccess = await onSubmit(payload);

      if (isSuccess) {
        resetForm();
      }
    }
  });

  const { values, errors, handleSubmit, handleChange, setFieldValue } = form;

  return (
    <form className={'webhook-table'} onSubmit={handleSubmit}>
      {/* Fake fields to prevent autocomplete */}
      <input type={'text'} name={'fakeusername'} style={{ display: 'none' }} tabIndex={'-1'} autoComplete={'off'} />
      <input
        type={'password'}
        name={'fakepassword'}
        style={{ display: 'none' }}
        tabIndex={'-1'}
        autoComplete={'new-password'}
      />

      <Table responsive>
        <tbody className={'webhook-table-body'}>
          <tr className={'webhook-table-row'}>
            <td className={'webhook-table-cell webhook-table-label'}>
              {i18n.getMessage('webhookTab.input.webhookUrl')}
            </td>
            <td className={'webhook-table-cell'}>
              <div className={'table-input-wrapper'}>
                <Input
                  className={'table-cell-input'}
                  name={'webhookUrl'}
                  value={values.webhookUrl}
                  onChange={handleChange}
                  error={errors?.webhookUrl}
                  autoComplete={'new-password'}
                  isDisabled={isLoading}
                />
              </div>
            </td>
          </tr>
          <tr className={'webhook-table-row'}>
            <td className={'webhook-table-cell webhook-table-label'}>
              {i18n.getMessage('webhookTab.input.webhookSecret')}
            </td>
            <td className={'webhook-table-cell'}>
              <div className={'table-input-wrapper'}>
                <Input
                  className={'table-cell-input'}
                  name={'webhookSecret'}
                  value={values.webhookSecret}
                  onChange={handleChange}
                  error={errors?.webhookSecret}
                  placeholder={isEditMode ? i18n.getMessage('webhookTab.input.webhookSecretPlaceholder') : ''}
                  autoComplete={'new-password'}
                  isDisabled={isLoading}
                />
              </div>
            </td>
          </tr>
          <tr className={'webhook-table-row'}>
            <td className={'webhook-table-cell webhook-table-label'}>
              {i18n.getMessage('webhookTab.input.direction')}
            </td>
            <td className={'webhook-table-cell'}>
              <div className={'table-input-wrapper'}>
                <InputDropDown
                  className={'table-cell-input webhook-direction-input'}
                  name={'direction'}
                  options={directionOptions}
                  value={values.direction}
                  error={errors?.direction}
                  onChange={(name, value) => setFieldValue(name, value)}
                  isDisabled={isLoading}
                />
                <p className={'webhook-direction-hint'}>
                  {i18n.getMessage(`webhookTab.direction.${values.direction}.description`)}
                </p>
              </div>
            </td>
          </tr>
          <tr className={'webhook-table-row'}>
            <td className={'webhook-table-cell'}></td>
            <td className={'webhook-table-cell'}>
              <div className={'webhook-actions-buttons-wrapper'}>
                <Button
                  className={'webhook-actions-button other-buttons'}
                  roleType={'submit'}
                  size={'small'}
                  type={'primary'}
                  onClick={() => {}}
                  isDisabled={isLoading}
                >
                  {i18n.getMessage(isEditMode ? 'webhookTab.button.update' : 'webhookTab.button.save')}
                </Button>

                {!isEditMode && (
                  <Button
                    className={'webhook-actions-button other-buttons'}
                    roleType={'button'}
                    size={'small'}
                    type={'outline'}
                    onClick={onCancel}
                    isDisabled={isLoading}
                  >
                    {i18n.getMessage('webhookTab.button.cancel')}
                  </Button>
                )}

                {isEditMode && (
                  <>
                    <Button
                      className={'webhook-actions-button other-buttons'}
                      roleType={'button'}
                      size={'small'}
                      type={'primary'}
                      onClick={onDelete}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('webhookTab.button.delete')}
                    </Button>

                    <Button
                      className={'webhook-actions-button test-buttons'}
                      roleType={'button'}
                      size={'small'}
                      type={'primary'}
                      onClick={() => onTest(TRANSACTION_TYPES.INCOMING)}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('webhookTab.button.testIncoming')}
                    </Button>

                    <Button
                      className={'webhook-actions-button test-buttons'}
                      roleType={'button'}
                      size={'small'}
                      type={'primary'}
                      onClick={() => onTest(TRANSACTION_TYPES.OUTGOING)}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('webhookTab.button.testOutgoing')}
                    </Button>
                  </>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </Table>
    </form>
  );
};

WebhookForm.propTypes = {
  webhook: PropTypes.shape({
    id: PropTypes.string,
    webhookUrl: PropTypes.string,
    direction: PropTypes.string
  }),
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  onTest: PropTypes.func
};
